import React, { useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme } from "@emotion/react";
import { Grid, Stack } from "@mui/material";
import rgba from "assets/theme/functions/rgba";

// Components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import ListComponent from "components/CBList";

// Styled Components
import { TwoColumnSection } from "styles/components/twoColumnSection";
import { StyledSectionContainer } from "styles/components/SectionContainer/index.styles";

// Utils
import useMediaQueries from "utils/mediaQueries.utils";
import { CartContext } from "utils/context/cart.context";
import { useStripeProducts } from "utils/context/stripe.context";
import { getProductFeatures, facilitatorContent, healingCenterContent } from "../../data/ehrContent";

// Animations
import { listItemVariants } from "styles/animations/staggeredList.animations";

const EHRProductSection = ({ setSelectedProduct, selectedProduct, sortedPackages, userType, shouldUpdateParentState = true }) => {
  const theme = useTheme();
  const { setIsCartOpen, addItemToCart } = useContext(CartContext);
  const { isLg } = useMediaQueries();
  const { ehrPackages } = useStripeProducts(); // Use real Stripe products

  // Filter EHR packages by userType
  const expectedUserType = userType === "facilitators" ? "facilitator" : "healing center";
  const filteredPackages = ehrPackages.filter((pkg) => pkg.metadata?.userType === expectedUserType);

  // Always show something - fallback to mock data if no real products match
  const currentPackages = filteredPackages.length > 0 ? filteredPackages : sortedPackages;

  const VideoComponent = () => {
    return (
      <Grid
        id="ehr-video-section"
        item
        xs={12}
        lg={6}
        sx={{ overflow: "visible" }}
        component={motion.div}
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <MKBox
          sx={{
            height: { xs: "100%", md: "419px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            padding: { xs: "0.5rem", sm: "1rem" },
            paddingTop: { xs: "0", sm: "0", md: "0" },
          }}
        >
          {/* Loom Video Embed */}
          <MKBox
            component="div"
            tabIndex={0}
            role="region"
            aria-label={userType === "healingCenters" ? "EHR system demo video for healing centers" : "EHR system demo video for facilitators"}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              minHeight: { xs: "250px", md: "350px" },
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: `0px 8px 25px ${rgba(theme.palette.accent.main, 0.3)}`,
              backgroundColor: theme.palette.grey[100],
              "&:focus": {
                outline: `2px solid ${theme.palette.accent.main}`,
                outlineOffset: "2px",
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.accent.main}`,
                outlineOffset: "2px",
              },
            }}
          >
            {/* Hidden description for screen readers */}
            <div
              id={`ehr-video-description-${userType}`}
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {userType === "healingCenters"
                ? "Interactive demo video showing the EHR system features for healing centers, including patient management, scheduling, and compliance tools. Video player controls: Play/pause button located at bottom left, volume control at bottom left, progress bar at bottom center, fullscreen button at bottom right. Use spacebar to play/pause, left/right arrows to skip forward/backward."
                : "Interactive demo video showing the EHR system features for facilitators, including appointment booking, patient flow automation, and compliance management. Video player controls: Play/pause button located at bottom left, volume control at bottom left, progress bar at bottom center, fullscreen button at bottom right. Use spacebar to play/pause, left/right arrows to skip forward/backward."}
            </div>

            <iframe
              src={
                userType === "healingCenters"
                  ? "https://www.loom.com/embed/df0873c20bcf41298c2d8700629c0702?sid=e8c1969d-5a50-4600-86e8-983c89760b37&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
                  : "https://www.loom.com/embed/dbd7c9923205454698d098e20a78d9f7?sid=c86d9c28-2688-4bad-862a-459c93685626&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
              }
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              allow="fullscreen; picture-in-picture"
              sandbox="allow-scripts allow-same-origin allow-presentation"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              title={
                userType === "healingCenters"
                  ? "EHR System Demo Video for Healing Centers - Interactive video player with accessible controls"
                  : "EHR System Demo Video for Facilitators - Interactive video player with accessible controls"
              }
              aria-label={
                userType === "healingCenters"
                  ? "EHR system demo video for healing centers. Video player includes labeled play/pause, volume, progress, and fullscreen controls. Use Space to play/pause and arrow keys to navigate."
                  : "EHR system demo video for facilitators. Video player includes labeled play/pause, volume, progress, and fullscreen controls. Use Space to play/pause and arrow keys to navigate."
              }
              aria-describedby={`ehr-video-description-${userType}`}
              role="application"
            />
          </MKBox>
        </MKBox>
      </Grid>
    );
  };

  const ContentComponent = () => {
    const currentSelectedProduct = selectedProduct || currentPackages[0];
    const { metadata } = currentSelectedProduct || { metadata: {} };
    const { isMd } = useMediaQueries();

    // Note: EHR products currently default to trial pricing display

    const handleAddToCart = () => {
      if (currentSelectedProduct) {
        // Ensure EHR products have the correct structure for cart
        const cartProduct = {
          ...currentSelectedProduct,
          // Keep the original metadata.type as "ehr-subscription" for proper validation
          // Ensure we have required display fields
          images: currentSelectedProduct.images || ["https://d3a0rs3velfrz.cloudfront.net/logos/almadelicButterFlyTransparent.webp"],
          metadata: {
            ...currentSelectedProduct.metadata,
            priceValue: `$${(currentSelectedProduct.price / 100).toFixed(2)}/month`,
            product_description:
              currentSelectedProduct.description ||
              `EHR ${currentSelectedProduct.metadata?.userType || "subscription"} package with comprehensive features.`,
          },
        };
        addItemToCart(cartProduct);
        setIsCartOpen(true);
      }
    };

    useEffect(() => {
      if (currentSelectedProduct && shouldUpdateParentState) {
        setSelectedProduct(currentSelectedProduct);
      }
    }, [currentSelectedProduct]);

    // Get dynamic content based on toggle userType (not product userType)
    const isHealing = userType === "healingCenters";
    const currentContent = isHealing ? healingCenterContent : facilitatorContent;
    const sectionDescription = currentContent.content.benefitsSection.description;

    // Get features based on the toggle userType
    const features = getProductFeatures(isHealing ? "healing center" : "facilitator");

    return (
      <>
        <Grid
          id="ehr-product-details-section"
          item
          xs={12}
          lg={5}
          sx={{ height: { lg: "419px" } }}
          component={motion.div}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        >
          {/* Show headers on desktop (when both sections displayed) and mobile */}
          {/* Debug: shouldUpdateParentState=${shouldUpdateParentState}, isLg=${isLg} */}
          {((shouldUpdateParentState && !isLg) || (!shouldUpdateParentState && isLg)) && (
            <MKBox
              sx={{
                borderBottom: `2px solid ${theme.palette.accent.main}`,
                paddingBlock: ".5rem !important",
                marginBottom: "1rem !important",
                marginTop: { xs: "1rem !important", lg: "0 !important" },
              }}
            >
              <MKTypography
                component="span"
                variant="h6"
                gutterBottom
                sx={{
                  color: `${theme.palette.accent.main} !important`,
                  display: "block",
                }}
              >
                {isHealing ? "What's Included in Healing Center Plans?" : "What's Included in Facilitator Plans?"}
              </MKTypography>
              <MKTypography
                component="h2"
                variant="h3"
                sx={{
                  color: `${theme.palette.primary.main} !important`,
                  display: "block",
                }}
              >
                {isHealing ? "Healing Center EHR Package" : "Facilitator EHR Package"}
              </MKTypography>
            </MKBox>
          )}

          <MKTypography
            variant="body1"
            sx={{
              color: theme.palette.grey[200],
              marginBottom: "1.5rem",
              lineHeight: 1.6,
            }}
          >
            {sectionDescription}
          </MKTypography>

          <ListComponent
            motionProps={{
              variants: {
                listItem: listItemVariants,
                list: {
                  hidden: { opacity: 0 },
                  show: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.15,
                      delayChildren: 0.2,
                      when: "beforeChildren",
                    },
                  },
                },
              },
              animate: "show",
              initial: "hidden",
            }}
            listItems={features}
            textColor={theme.palette.primary.main}
            icon={
              <MKBox
                component="img"
                sx={{ width: "24px", marginRight: ".5rem !important" }}
                src="https://d3a0rs3velfrz.cloudfront.net/logos/almadelicButterFlyTransparent.webp"
                alt="Almadelic butterfly bullet point icon"
              />
            }
          />

          <Stack
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.5 }}
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleAddToCart();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Add ${userType === "healingCenters" ? "healing center" : "facilitator"} EHR package to cart - Free 60 day trial, then $${(
              currentSelectedProduct?.price / 100
            ).toFixed(2)} per month`}
            sx={{
              padding: ".75rem 1.5rem",
              background: theme.palette.secondary.main,
              borderRadius: "12px",
              marginBottom: "1rem",
              cursor: "pointer",
              transition: "all 0.3s ease",
              userSelect: "none",
              "&:hover": {
                background: theme.palette.accent.main,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${rgba(theme.palette.accent.main, 0.3)}`,
                "& .price-text": {
                  display: "none",
                },
                "& .trial-text": {
                  display: "block",
                  color: "#ffffff",
                },
              },
              "&:focus": {
                outline: `2px solid ${theme.palette.accent.main}`,
                outlineOffset: "2px",
              },
            }}
          >
            <MKTypography
              component="div"
              variant="h6"
              className="price-text"
              sx={{
                fontWeight: "bold",
                color: "#ffffff !important",
                transition: "all 0.3s ease",
              }}
            >
              Free Prelaunch Access
            </MKTypography>
            <MKTypography
              component="div"
              variant="h6"
              className="trial-text"
              sx={{
                fontWeight: "bold",
                color: "#ffffff !important",
                display: "none",
                transition: "all 0.3s ease",
              }}
            >
              Start
            </MKTypography>
          </Stack>
        </Grid>
      </>
    );
  };

  // Conditionally swap components based on userType and screen size
  const isFacilitator = userType === "facilitators";
  const videoComponent = <VideoComponent />;
  const contentComponent = <ContentComponent />;

  // On mobile (isLg is false), always show video on top (left component)
  // On desktop (isLg is true), use the original logic
  const shouldVideoBeOnLeft = !isLg || !isFacilitator;

  return (
    <StyledSectionContainer cssProps={{ paddingTop: "2rem" }} id="ehr-packages-product-section">
      <TwoColumnSection
        cssProps={{
          alignItems: "flex-start !important",
        }}
        rowGap={{ xs: 2, sm: 4, lg: 6 }}
        columnGap={{ xs: 3, sm: 4, lg: 6 }}
        leftComponent={shouldVideoBeOnLeft ? videoComponent : contentComponent}
        rightComponent={shouldVideoBeOnLeft ? contentComponent : videoComponent}
      />
    </StyledSectionContainer>
  );
};

export default EHRProductSection;
